import React, { useContext, useEffect, useState, useCallback, useRef } from "react";
import { useHistory } from "react-router-dom";
import { AuthContext } from "../context/Auth/AuthContext";

// @mui
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
// hooks
import useCollapseDrawer from '../hooks/useCollapseDrawer';
import useResponsive from '../hooks/useResponsive';
import useSettings from '../hooks/useSettings';
// config
import { HEADER, NAVBAR } from '../config';
//
import DashboardHeader from './header';
import NavbarVertical from './navbar/NavbarVertical';
import KeepSocketAlive from "../hooks/KeepSocketAlive";
import socket from "../hooks/useSocket";
import CalendarEventNotification from "../components/CalendarEventNotification";
import NewVersionModal from "../components/NewVersionModal";
import MultiTabDetector from "../helpers/MultiTabDetector";
import api from "../services/api";

// ----------------------------------------------------------------------

const MainStyle = styled('main', {
  shouldForwardProp: (prop) => prop !== 'collapseClick',
})(({ collapseClick, theme }) => ({
  flexGrow: 1,
  paddingTop: HEADER.MOBILE_HEIGHT,
  [theme.breakpoints.up('lg')]: {
    paddingTop: HEADER.DASHBOARD_DESKTOP_HEIGHT - 25,
    width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH}px)`,
    transition: theme.transitions.create('margin-left', {
      duration: theme.transitions.duration.shorter,
    }),
    ...(collapseClick && {
      marginLeft: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
    }),
  },
}));

// ----------------------------------------------------------------------

// Constantes
// O BACKEND é a autoridade do online↔ausente: decide pela última atividade
// AGREGADA de todas as sessões do usuário. O frontend NÃO decide mais "ausente"
// sozinho (isso causava flapping com múltiplas sessões) — só REPORTA atividade.
const ACTIVITY_PING_MS = 25 * 1000; // ping de atividade enquanto ativo
const STATUS = {
  OFFLINE: 0,
  ONLINE: 1,
  AWAY: 2,
  BUSY: 3
};

const LoggedInLayout = ({ children }) => {
  const history = useHistory();
  const { user, status, handleStatus } = useContext(AuthContext);

  // Modal de descontinuação / nova versão — só para a empresa 2.
  const [newVersionOpen, setNewVersionOpen] = useState(false);
  useEffect(() => {
    if (Number(user?.companyId) === 2) setNewVersionOpen(true);
  }, [user?.companyId]);

  // Enquanto OCUPADO (3) o controle automático fica suspenso. Ref pra não
  // recriar o activity-tracking a cada mudança de status.
  const busyRef = useRef(false);
  // Status AUTORITATIVO (do backend/broadcast) — o activity-tracking compara com
  // ele pra decidir emitir, corrigindo o caso multi-aba (aba ativa re-emite
  // online mesmo que outra aba a tenha jogado pra ausente).
  const statusRef = useRef(0);
  // Aponta pro handleActivity atual — usado pra rearmar o timer ao sair do ocupado.
  const kickActivityRef = useRef(null);
  useEffect(() => {
    const s = Number(status);
    const wasBusy = busyRef.current;
    busyRef.current = s === STATUS.BUSY;
    statusRef.current = s;
    // Saiu de ocupado → religa o automático imediatamente (rearma o idle timer).
    if (wasBusy && s !== STATUS.BUSY) kickActivityRef.current?.();
  }, [status]);
  const { collapseClick, isCollapse } = useCollapseDrawer();
  const { themeLayout } = useSettings();
  const isDesktop = useResponsive('up', 'lg');

  // Estados
  const [sidebarOpen, setSidebarOpen] = useState(isDesktop);
  const [eventData, setEventData] = useState(null);
  const [eventModalOpen, setEventModalOpen] = useState(false);

  // Hook para verificar múltiplas abas
  useEffect(() => {
    const detector = new MultiTabDetector();
    detector.isMultipleTabs((isMultiple) => {
      if (isMultiple) {
        history.push('/session/error');
      }
    });
  }, [history]);

  // Função para buscar dados do evento
  const getEventData = useCallback(async (eventId) => {
    try {
      const { data } = await api.get(`/calendar/${eventId}`);
      setEventData(data);
      setEventModalOpen(true);
    } catch (error) {
      console.error("Erro ao buscar dados do evento:", error);
    }
  }, []);

  // Gerenciamento de atividade do usuário — o frontend só REPORTA atividade;
  // quem decide ausente é o backend (pela atividade agregada de todas as sessões).
  // Em cada input: volta a ONLINE na hora se o backend tinha marcado ausente, e
  // manda um ping "activity" (no máx. 1 a cada ACTIVITY_PING_MS) pra o backend
  // saber que esta sessão segue ativa. Sem timer de ausência no front → sem flap.
  const setupUserActivity = useCallback(() => {
    if (!user?.id) return { cleanup: () => { } };

    let lastPingAt = 0;

    // Volta a online só se o status autoritativo não é online (dedup). statusRef
    // vem do broadcast do backend.
    const emitOnline = () => {
      if (statusRef.current === STATUS.ONLINE) return;
      socket.emit('change_status', { userId: user.id, status: STATUS.ONLINE });
      statusRef.current = STATUS.ONLINE;
    };

    const handleActivity = () => {
      if (busyRef.current) return; // ocupado: automático suspenso
      emitOnline();
      const now = Date.now();
      if (now - lastPingAt >= ACTIVITY_PING_MS) {
        socket.emit('activity', { userId: user.id });
        lastPingAt = now;
      }
    };

    // Expõe o handleActivity atual pra "religar" ao sair do ocupado.
    kickActivityRef.current = handleActivity;

    // Fontes de atividade: ponteiro, clique, teclado, toque, roda e ROLAGEM
    // cobrem o uso real — inclusive LER/ROLAR tickets sem mexer o mouse (senão
    // a pessoa ia pra ausente lendo). 'scroll' com capture:true pega rolagem em
    // containers internos (scroll não borbulha). visibilitychange conta como
    // atividade quando a aba volta a ficar visível.
    window.addEventListener('mousemove', handleActivity, { passive: true });
    window.addEventListener('mousedown', handleActivity, { passive: true });
    window.addEventListener('keydown', handleActivity, { passive: true });
    window.addEventListener('touchstart', handleActivity, { passive: true });
    window.addEventListener('wheel', handleActivity, { passive: true });
    window.addEventListener('scroll', handleActivity, { passive: true, capture: true });
    const onVisible = () => { if (document.visibilityState === 'visible') handleActivity(); };
    document.addEventListener('visibilitychange', onVisible);

    // Iniciar: garante online + 1º ping de atividade.
    handleActivity();

    return {
      cleanup: () => {
        kickActivityRef.current = null;
        window.removeEventListener('mousemove', handleActivity);
        window.removeEventListener('mousedown', handleActivity);
        window.removeEventListener('keydown', handleActivity);
        window.removeEventListener('touchstart', handleActivity);
        window.removeEventListener('wheel', handleActivity);
        window.removeEventListener('scroll', handleActivity, { capture: true });
        document.removeEventListener('visibilitychange', onVisible);
      }
    };
  }, [user?.id]);

  // Gerenciamento de conexão socket
  useEffect(() => {
    if (!user?.companyId || !user?.id) return;

    socket.connect(user.companyId, user.id);

    // Função para registrar usuário no socket
    const registerUser = () => {
      socket.emit('register', { userId: user.id });
    };

    // Configuração de ouvintes de eventos socket
    socket.on('connect', registerUser);

    socket.on(`user_status_${user.companyId}`, (data) => {
      if (data.id === user.id) {
        handleStatus(data.status, data.statusMessage);
      }
    });

    socket.on(`calendar`, (data) => {
      const { action, calendarId, users } = data;
      if (action === "notification" && users.includes(user.id)) {
        getEventData(calendarId);
      }
    });

    // Configuração de monitoramento de atividade
    const { cleanup: cleanupActivity } = setupUserActivity();

    // Ajustar sidebarOpen baseado na largura da janela
    const handleResize = () => {
      setSidebarOpen(window.innerWidth > 600);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      socket.off('connect', registerUser);
      socket.off(`user_status_${user.companyId}`);
      socket.off(`calendar`);
      cleanupActivity();
      window.removeEventListener('resize', handleResize);
    };
  }, [user, getEventData, setupUserActivity, handleStatus]);

  return (
    <Box
      sx={{
        display: { lg: 'flex' },
        minHeight: { lg: 1 },
      }}
    >
      {user?.companyId && user?.id && (
        <KeepSocketAlive companyId={user.companyId} userId={user.id} />
      )}

      <div className="pageMenu">
        <DashboardHeader
          isCollapse={isCollapse}
          onOpenSidebar={() => setSidebarOpen(true)}
        />
        <NavbarVertical
          isOpenSidebar={sidebarOpen}
          onCloseSidebar={() => setSidebarOpen(false)}
        />
      </div>

      <MainStyle collapseClick={collapseClick} id="layout">
        {children}
      </MainStyle>

      {eventData && (
        <CalendarEventNotification
          event={eventData}
          open={eventModalOpen}
          onClose={() => setEventModalOpen(false)}
          onConfirm={() => console.info("Evento confirmado")}
        />
      )}

      <NewVersionModal open={newVersionOpen} onClose={() => setNewVersionOpen(false)} />
    </Box>
  );
};

export default LoggedInLayout;